<script setup lang="ts">
/**
 * The one dialog that stands between a click and the end of the session.
 *
 * Rendered once near the root in `App.vue` and driven entirely by
 * `state/confirm-sign-out` — it has no props and knows nothing about the
 * screen that asked, which is what lets every Sign out button share it.
 *
 * Three things about it are load-bearing.
 *
 * **Escape and the backdrop both decline**, and the keydown is stopped rather
 * than allowed to bubble, so a host screen with its own window-level Escape
 * listener does not also react to the keypress that closed this dialog.
 *
 * **Focus moves to Cancel, and the confirm button is not autofocused.** A
 * Return key on an autofocused confirm is exactly the accident this exists to
 * prevent.
 *
 * **The confirm is red rather than the yellow paint.** This is the app's one
 * destructive control; it must not borrow the colour that means "go". Staying
 * is the quiet plate beside it.
 *
 * It is a small game window (`GameWindow`) with the shield icon, the warning
 * in two short lines, and the two answers on plates beneath.
 */
import { nextTick, ref, watch, type ComponentPublicInstance } from 'vue';

import { useI18n } from '@/i18n';
import { cancelSignOut, signOutAsked } from '@/state/confirm-sign-out';
import { useSession } from '@/state/session';
import GameWindow from './kit/GameWindow.vue';
import ActionButton from './panel/ActionButton.vue';
import SheetTransition from './panel/SheetTransition.vue';

const session = useSession();
const { t } = useI18n();

/** Cancel is a component; its root element is the button that takes focus. */
const cancelButton = ref<ComponentPublicInstance | null>(null);

/**
 * True from the moment Sign out is pressed until the session is gone.
 *
 * The request is a round trip, and a second press would send a second one. It
 * also stops the dialog reading as unresponsive on a slow connection: the
 * screen behind it does not change until `player` is null.
 */
const going = ref(false);

watch(signOutAsked, async (open) => {
  if (!open) return;
  going.value = false;
  await nextTick();
  (cancelButton.value?.$el as HTMLElement | undefined)?.focus?.();
});

function onKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Escape') return;
  // See the note above: a host's window-level Escape listener must not see this.
  event.stopPropagation();
  if (!going.value) cancelSignOut();
}

async function confirm(): Promise<void> {
  if (going.value) return;
  going.value = true;
  await session.logout();
  // Closed after the session is gone, so the dialog is never left standing over
  // a screen that has already swapped underneath it.
  cancelSignOut();
}
</script>

<template>
  <SheetTransition>
    <GameWindow
      v-if="signOutAsked"
      width="24rem"
      level="alert"
      role="alertdialog"
      aria-modal="true"
      data-test="confirm-sign-out"
      :title="t('confirm.signOut.title')"
      icon="anchor"
      @close="going || cancelSignOut()"
      @keydown="onKeydown"
    >
      <p class="says">{{ t('confirm.signOut.body') }}</p>

      <template #actions>
        <ActionButton ref="cancelButton" size="sm" data-test="sign-out-cancel" :disabled="going" @click="cancelSignOut()">
          {{ t('confirm.cancel') }}
        </ActionButton>
        <ActionButton variant="danger" size="sm" data-test="sign-out-confirm" :disabled="going" :loading="going" @click="confirm()">
          {{ going ? t('confirm.signOut.going') : t('confirm.signOut.title') }}
        </ActionButton>
      </template>
    </GameWindow>
  </SheetTransition>
</template>

<style scoped>
.says {
  margin: 0;
  font-family: var(--font-game-body);
  font-size: 0.95rem;
  font-weight: 500;
  line-height: 1.5;
  text-align: center;
  color: #dde6ee;
}
</style>
